import { createAudioTestRunner } from './audioTestRunner';
import { getTemplate, getProtocol } from '../templates/testTemplates';
import { voiceSupported } from './voice';

export const sessionKey = (userId, teamId) => `trainnova:test-draft:v1:${encodeURIComponent(userId)}:${encodeURIComponent(teamId)}`;
export const playerKey = p => p.player_id || p.id || p.player_name;
export const timerElapsed = (timer, total, now = Date.now()) => Math.min(total,
  Math.max(0, (timer?.elapsed || 0) + (timer?.startedAt ? Math.max(0, now - timer.startedAt) / 1000 : 0)));
const empty = () => ({ version: 1, step: 'choose', selection: { selected: {}, guests: [] },
  finished: {}, warnings: {}, pending: null, manual: {}, timer: { elapsed: 0, startedAt: null },
  results: null, comments: '', confirmed: false, savePayload: null, voiceEnabled: voiceSupported() });

export function readDraft(storage, key, userId, teamId) {
  const draft = JSON.parse(storage.getItem(key) || 'null');
  if (!draft) return null;
  if (draft.version !== 1 || draft.userId !== userId || draft.teamId !== teamId ||
      !getTemplate(draft.templateId) || !['players', 'setup', 'run', 'review'].includes(draft.step) ||
      !draft.sessionId || !draft.selection?.selected || !Array.isArray(draft.selection.guests)) {
    throw new Error('Det sparade testpasset kan inte läsas. Det har inte skrivits över.');
  }
  return draft;
}

// One store per account/team, held above Routes. Only a lock owner may create it.
// All user edits write synchronously; clock checkpoints are written once/second.
export function createTestSessionStore({ userId, teamId, storage, now = Date.now, makeRunner = createAudioTestRunner }) {
  const key = sessionKey(userId, teamId), listeners = new Set();
  let draft = { ...empty(), userId, teamId }, runner = null, destroyed = false, checkpoint = 0;
  let audioState = null, audioStatus = 'idle', audioError = null, storageError = null;
  let restored = false, unreadable = false, snapshot;
  try { const saved = readDraft(storage, key, userId, teamId); if (saved) { draft = { ...draft, ...saved }; restored = true; } }
  catch (e) { storageError = e.message; unreadable = true; }
  function emit() {
    snapshot = { draft, audioState, audioStatus, audioError, storageError, restored, unreadable };
    listeners.forEach(fn => fn());
  }
  function persist() {
    if (unreadable) return;
    if (runner) draft = { ...draft, audio: runner.getSnapshot() };
    try {
      if (draft.step === 'choose') storage.removeItem(key);
      else storage.setItem(key, JSON.stringify({ ...draft, updatedAt: now() }));
      storageError = null;
    } catch { storageError = 'Utkastet kunde inte sparas på enheten. Behåll appen öppen tills resultaten har sparats.'; }
  }
  function patch(values) {
    if (destroyed || unreadable) return;
    draft = { ...draft, ...(typeof values === 'function' ? values(draft) : values) };
    persist(); emit();
  }
  function createRunner() {
    if (runner) return;
    const template = getTemplate(draft.templateId), protocol = getProtocol(template?.protocolId);
    if (!protocol || !['run', 'review'].includes(draft.step)) return;
    const protocolChanged = draft.protocolVersion && draft.protocolVersion !== protocol.protocolVersion;
    if (protocolChanged) {
      audioError = 'Testprotokollet har ändrats. Granska sparade resultat innan du startar ett nytt pass.';
      audioStatus = 'error'; return;
    }
    runner = makeRunner(protocol, {
      voiceEnabled: draft.voiceEnabled, snapshot: draft.audio,
      onState: st => {
        if (destroyed) return;
        audioState = st;
        if (now() - checkpoint >= 1000) { checkpoint = now(); persist(); }
        emit();
      },
      onStatus: status => { audioStatus = status; persist(); emit(); },
      onError: e => { audioError = e.message || 'Ljudet avbröts.'; persist(); emit(); },
    });
    audioStatus = runner.getStatus(); audioState = runner.getState();
    if (restored && draft.audio && ['running', 'starting', 'error'].includes(draft.audio.status))
      audioError = 'Testpasset har återställts efter att sidan stängdes eller laddades om. Kontrollera resultaten och fortsätt när spelarna är redo.';
    persist();
  }
  createRunner(); emit();
  const store = {
    subscribe: fn => { listeners.add(fn); return () => listeners.delete(fn); },
    getSnapshot: () => snapshot,
    patch,
    choose(template) {
      if (draft.step !== 'choose' || unreadable) return;
      patch({ templateId: template.id, protocolVersion: getProtocol(template.protocolId)?.protocolVersion || template.protocolVersion,
        sessionId: globalThis.crypto?.randomUUID?.() || `${now()}-${Math.random().toString(36).slice(2)}`,
        createdAt: new Date(now()).toISOString(), step: 'players' });
    },
    setStep(step) { patch({ step }); createRunner(); emit(); },
    players: () => [...Object.values(draft.selection.selected), ...draft.selection.guests],
    async start() { createRunner(); audioError = null; emit(); return runner?.start(); },
    async pause(markInterrupted = true) { await runner?.pause(markInterrupted); if (!destroyed) { persist(); emit(); } },
    async resume() { if (!runner) return false; audioError = null; emit(); const ok = await runner.resume(); if (!destroyed) { persist(); emit(); } return ok; },
    setVoiceEnabled(value) { runner?.setVoiceEnabled(value); patch({ voiceEnabled: value }); },
    getResultNow: () => runner?.getResultNow(),
    getSchedule: () => runner?.schedule,
    resetAudio() { runner?.reset(); audioError = null; patch({ finished: {}, warnings: {}, pending: null }); },
    confirmStop(value) {
      const pending = draft.pending;
      if (!pending) return;
      const finished = { ...draft.finished, [playerKey(pending.player)]: {
        ...pending.snapshot, ...value, displayLevel: `${value.level}.${value.shuttle}`, confirmed: true,
      }};
      patch({ finished, pending: null });
      if (store.players().every(p => finished[playerKey(p)])) store.pause(false);
    },
    async reviewAudio() {
      if (!runner || ['idle', 'starting'].includes(audioStatus)) return;
      await store.pause(false);
      if (destroyed || !runner) return;
      const current = runner.getResultNow(), template = getTemplate(draft.templateId);
      const results = store.players().map(p => {
        const r = draft.finished[playerKey(p)] || current;
        return { player_id: p.player_id || p.id || null, player_name: p.player_name, is_guest: !!p.is_guest,
          interrupted: !!current.interrupted, result_confirmed: !!r.confirmed, status: 'completed',
          final_level: r.level, final_shuttle: r.shuttle, final_distance: r.distance, stop_time: r.stopTime,
          manual_override: false, unit: template.unit };
      });
      patch({ results, confirmed: false, step: 'review' });
    },
    startTimer() {
      const total = getTemplate(draft.templateId)?.durationSeconds || 720;
      const elapsed = timerElapsed(draft.timer, total, now());
      if (elapsed < total && !draft.timer.startedAt) patch({ timer: { elapsed, startedAt: now() } });
    },
    pauseTimer() { patch({ timer: { elapsed: timerElapsed(draft.timer, getTemplate(draft.templateId)?.durationSeconds || 720, now()), startedAt: null } }); },
    resetTimer() { patch({ timer: { elapsed: 0, startedAt: null } }); },
    checkpoint() { if (!destroyed) { persist(); emit(); } },
    leaveDocument() { if (runner && ['running', 'starting'].includes(audioStatus)) store.pause(true); persist(); emit(); },
    complete(sessionId) { if (destroyed || draft.sessionId !== sessionId) return false; store.discard(); return true; },
    discard() {
      if (destroyed) return;
      if (runner) { runner.destroy(); runner = null; }
      unreadable = false; restored = false; audioState = null; audioStatus = 'idle'; audioError = null;
      draft = { ...empty(), userId, teamId }; persist(); emit();
    },
    destroy() {
      if (destroyed) return;
      // A team/account change ends audio ownership, never the saved draft.
      store.leaveDocument(); persist(); destroyed = true; runner?.destroy(); runner = null; listeners.clear();
    },
  };
  return store;
}
